import React, { useCallback, useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiCpu, FiCheck, FiRefreshCw } from 'react-icons/fi';
import { ROUTES } from '../../app/routes';
import { getHarnessTypeConfig, upsertHarnessTypeConfig, HarnessType, } from '../../services/companySettingsService';
import '../../styles/Settings.css';
const HARNESS_TYPE_OPTIONS: {
    value: HarnessType;
    label: string;
}[] = [
    { value: 'Claude', label: 'Claude' },
    { value: 'Hermes', label: 'Hermes' },
];
const HarnessTypeSettingsPage: React.FC = () => {
    const navigate = useNavigate();
    const [harnessType, setHarnessType] = useState<HarnessType>('Claude');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [saved, setSaved] = useState(false);
    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        getHarnessTypeConfig()
            .then((config) => {
            if (cancelled)
                return;
            if (config?.harnessType)
                setHarnessType(config.harnessType);
        })
            .catch(() => {
        })
            .finally(() => {
            if (!cancelled)
                setLoading(false);
        });
        return () => {
            cancelled = true;
        };
    }, []);
    const handleSave = useCallback(() => {
        if (saving)
            return;
        setSaving(true);
        setError('');
        setSaved(false);
        upsertHarnessTypeConfig(harnessType)
            .then(() => {
            setSaved(true);
            window.setTimeout(() => setSaved(false), 2500);
        })
            .catch(() => setError('Could not save changes. Please try again.'))
            .finally(() => setSaving(false));
    }, [harnessType, saving]);
    return (<div className="s2-page">
      <motion.div className="s2-container" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }}>
        <section className="s2-top-header">
          <div className="s2-top-header__text">
            <h1 className="s2-top-header__title">Harness Type</h1>
            <p className="s2-top-header__subtitle">
              Choose which agent harness powers your Digital Workforce. This applies to your whole company.
            </p>
          </div>
          <div className="s2-top-header__actions">
            <button className="s2-top-header__nav-btn" onClick={() => navigate(ROUTES.SETTINGS)}>
              <FiArrowLeft size={14}/>
              <span>Back to Settings</span>
            </button>
          </div>
        </section>

        <motion.section className="s2-section" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }}>
          <div className="s2-section-label">
            <span className="s2-section-dot s2-section-dot--purple"/>
            Digital Workforce
          </div>

          <div className="s2-appearance-card">
            <div className="s2-appearance-left">
              <div className="s2-appearance-icon-wrap">
                {loading ? (<FiRefreshCw size={20} style={{ animation: 'spin 1s linear infinite' }}/>) : (<FiCpu size={20}/>)}
              </div>
              <div>
                <div className="s2-appearance-title">Harness Type</div>
                <div className="s2-appearance-sub">
                  {loading ? 'Loading current setting…' : 'Select the agent harness used to run your Digital Workforce'}
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              {loading && (<FiRefreshCw size={15} style={{ animation: 'spin 1s linear infinite', color: 'var(--text-tertiary)' }}/>)}
              <select value={harnessType} disabled={loading || saving} onChange={(e) => setHarnessType(e.target.value as HarnessType)} style={{
            padding: '10px 14px',
            borderRadius: 10,
            border: '1px solid var(--border-glass)',
            background: 'var(--bg-primary)',
            color: 'var(--text-primary)',
            fontSize: 13,
            fontWeight: 600,
            fontFamily: 'inherit',
            outline: 'none',
            cursor: loading || saving ? 'not-allowed' : 'pointer',
            minWidth: 160,
        }}>
                {HARNESS_TYPE_OPTIONS.map((opt) => (<option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>))}
              </select>

              <button onClick={handleSave} disabled={loading || saving} style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 7,
            padding: '10px 18px',
            borderRadius: 10,
            border: 'none',
            fontSize: 13,
            fontWeight: 600,
            fontFamily: 'inherit',
            cursor: loading || saving ? 'not-allowed' : 'pointer',
            background: 'linear-gradient(135deg, #6d28d9, #a200ff)',
            color: '#fff',
            opacity: loading || saving ? 0.7 : 1,
            whiteSpace: 'nowrap',
        }}>
                {saving && (<FiRefreshCw size={13} style={{ animation: 'spin 1s linear infinite' }}/>)}
                {saving ? 'Saving…' : saved ? (<>
                    <FiCheck size={13}/> Saved
                  </>) : ('Save changes')}
              </button>
            </div>
          </div>

          {error && (<p style={{ margin: '10px 0 0', fontSize: 12, color: '#ef4444' }}>{error}</p>)}
        </motion.section>
      </motion.div>
    </div>);
};
export default HarnessTypeSettingsPage;
